import { useCallback, useMemo, useState } from "react";

import type {
  DetailsMode,
  EditBottomTab
} from "features/medical-scribe/medical-scribes-section/scribes-list/scribe-details-dialog/view-scribe-details/hooks/use-scribe-details-mode.hook";

/**
 * View Scribe vs Audio Recordings for the selected recording. The dialog owns this mode, not
 * the scribe details, because a list row's Edit opens a recording straight into edit mode.
 *
 * `detailsProps` hands the mode to `ViewScribeDetails` only when the paused feature is on. Off,
 * it is empty, so the details keep the mode themselves, as they did before the feature.
 *
 * Every open starts on view: the hosts mount the dialog only while `show` is true, so each open
 * is a fresh mount.
 */
export default function useInsertScribeDetailsMode({
  isPausedFeatureEnabled
}: {
  isPausedFeatureEnabled: boolean;
}) {
  const [detailsMode, setDetailsMode] = useState<DetailsMode>("view");
  const [editBottomTab, setEditBottomTab] = useState<EditBottomTab>("sessions");

  const resetToView = useCallback(() => {
    setDetailsMode("view");
    setEditBottomTab("sessions");
  }, []);

  const enterEdit = useCallback((bottomTab: EditBottomTab) => {
    setEditBottomTab(bottomTab);
    setDetailsMode("edit");
  }, []);

  const detailsProps = useMemo(
    () =>
      isPausedFeatureEnabled
        ? {
            detailsMode,
            onDetailsModeChange: setDetailsMode,
            editBottomTab,
            onEditBottomTabChange: setEditBottomTab,
            onEnterEdit: enterEdit
          }
        : {},
    [isPausedFeatureEnabled, detailsMode, editBottomTab, enterEdit]
  );

  return {
    isEditing: detailsMode === "edit",
    resetToView,
    enterEdit,
    detailsProps
  };
}
